import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Coins, ShieldCheck, Users, Award, Zap, Flame, Copy, Check, UserPlus, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const EARNING_RULES = [
  { icon: ShieldCheck, action: 'Protect a link', points: 1, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { icon: Users, action: 'Support a new creator', points: 5, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  { icon: Flame, action: 'Daily activity streak', points: 10, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  { icon: Award, action: 'Welcome bonus', points: 10, color: 'text-purple-400', bg: 'bg-purple-500/10' },
];

export default function PointsPage() {
  const { user, token, API } = useAuth();
  const [activities, setActivities] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showAllEarning, setShowAllEarning] = useState(false);
  const [showEarnings, setShowEarnings] = useState(false);

  const fetchActivities = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/points/activity?limit=30`, { headers: { Authorization: `Bearer ${token}` } });
      setActivities(res.data);
    } catch (e) { console.error(e); }
  }, [API, token]);

  const fetchReferralStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/referral/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setReferralStats(res.data);
    } catch (e) { console.error(e); }
  }, [API, token]);

  useEffect(() => { fetchActivities(); fetchReferralStats(); }, [fetchActivities, fetchReferralStats]);

  const level = user?.level || 1;
  const points = user?.points || 0;
  const pointsInLevel = points % 50;

  const referralLink = referralStats
    ? `${window.location.origin}/signup?ref=${referralStats.referral_code}`
    : '';

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const allEarningRules = [
    ...EARNING_RULES,
    ...(referralStats ? [{ icon: UserPlus, action: 'Refer a friend', points: referralStats.bonus_per_referral, color: 'text-pink-400', bg: 'bg-pink-500/10' }] : []),
  ];

  const visibleRules = showAllEarning ? allEarningRules : allEarningRules.slice(0, 3);

  return (
    <div className="p-5 space-y-4">
      {/* Points Overview */}
      <div className="glass-card rounded-xl p-5 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 to-transparent" />
        <div className="relative">
          <div className="w-14 h-14 mx-auto mb-2 hex-clip bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
            <Coins className="w-6 h-6 text-black" />
          </div>
          <p className="font-heading font-black text-3xl text-amber-500 mb-0.5" data-testid="points-total">{points}</p>
          <p className="text-[10px] text-zinc-500">Total Points</p>
          
          <div className="mt-3 flex items-center justify-center gap-5">
            <div>
              <p className="font-heading font-bold text-base text-white">{level}</p>
              <p className="text-[9px] text-zinc-500">Level</p>
            </div>
            <div className="w-px h-6 bg-white/10" />
            <div>
              <p className="font-heading font-bold text-base text-white">{50 - pointsInLevel}</p>
              <p className="text-[9px] text-zinc-500">To Next</p>
            </div>
            <div className="w-px h-6 bg-white/10" />
            <div className="flex items-center gap-1">
              <Flame className="w-3.5 h-3.5 text-orange-400" />
              <p className="font-heading font-bold text-base text-orange-400">{user?.daily_streak || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Level Progress + Refer Friends Combined */}
      <div className="glass-card rounded-xl p-4 space-y-4">
        {/* Level Progress */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-zinc-400">Level Progress</span>
            <span className="text-xs font-mono text-amber-500">{pointsInLevel}/50 XP</span>
          </div>
          <div className="flex gap-1">
            {Array.from({ length: 10 }).map((_, i) => (
              <motion.div
                key={i}
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ delay: i * 0.05 }}
                className={`flex-1 h-2.5 rounded-sm ${
                  i < Math.floor(pointsInLevel / 5)
                    ? 'bg-gradient-to-t from-amber-600 to-amber-500'
                    : 'bg-zinc-800'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Refer Friends */}
        {referralStats && (
          <div className="pt-3 border-t border-white/5" data-testid="referral-section">
            <div className="flex items-center gap-2 mb-2">
              <UserPlus className="w-4 h-4 text-pink-400" />
              <span className="text-xs font-bold text-pink-400">Refer Friends</span>
              {referralStats.total_referrals > 0 && (
                <span className="ml-auto px-2 py-0.5 rounded-full bg-pink-500/10 text-[9px] font-bold text-pink-400 border border-pink-500/20">
                  {referralStats.total_referrals} referred
                </span>
              )}
            </div>
            <p className="text-[10px] text-zinc-500 mb-2">
              Earn <span className="text-amber-400 font-bold">{referralStats.bonus_per_referral} pts</span> per friend who joins
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-[#09090B] border border-white/5 rounded-lg px-3 py-2 font-mono text-xs text-amber-500 truncate" data-testid="referral-code">
                {referralStats.referral_code}
              </div>
              <button
                onClick={copyReferralLink}
                className={`px-3 py-2 rounded-lg text-[10px] font-bold transition-all flex items-center gap-1.5 ${
                  copied
                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                    : 'bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20'
                }`}
                data-testid="referral-copy-btn"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            {referralStats.total_referrals > 0 && (
              <p className="mt-2 text-[9px] text-amber-500 font-bold text-right">+{referralStats.total_bonus_points} bonus pts earned</p>
            )}
          </div>
        )}
      </div>

      {/* How to Earn - Collapsible */}
      <div>
        <p className="text-xs font-bold text-zinc-400 mb-2">How to Earn</p>
        <div className="space-y-1.5">
          {visibleRules.map((rule, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
              className="flex items-center gap-3 glass-card rounded-lg px-3 py-2.5"
            >
              <div className={`w-7 h-7 rounded-lg ${rule.bg} flex items-center justify-center flex-shrink-0`}>
                <rule.icon className={`w-3.5 h-3.5 ${rule.color}`} />
              </div>
              <span className="text-xs text-zinc-300 flex-1">{rule.action}</span>
              <span className="text-xs font-mono font-bold text-amber-500">+{rule.points}</span>
            </motion.div>
          ))}
        </div>
        {allEarningRules.length > 3 && (
          <button
            onClick={() => setShowAllEarning(!showAllEarning)}
            className="w-full mt-2 py-2 text-[10px] text-zinc-500 hover:text-zinc-400 flex items-center justify-center gap-1"
            data-testid="show-more-earning"
          >
            {showAllEarning ? (
              <>Show less <ChevronUp className="w-3 h-3" /></>
            ) : (
              <>Show more <ChevronDown className="w-3 h-3" /></>
            )}
          </button>
        )}
      </div>

      {/* Recent Earnings - Collapsed by default */}
      {activities.length > 0 && (
        <div>
          <button
            onClick={() => setShowEarnings(!showEarnings)}
            className="w-full flex items-center justify-between py-2"
            data-testid="toggle-earnings"
          >
            <p className="text-xs font-bold text-zinc-400">Recent Earnings</p>
            <div className="flex items-center gap-1 text-[10px] text-zinc-500">
              <span>{activities.length} items</span>
              {showEarnings ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </div>
          </button>
          
          <AnimatePresence>
            {showEarnings && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="space-y-1 max-h-[180px] overflow-y-auto">
                  {activities.map((a, i) => (
                    <div
                      key={a.id || i}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/[0.02] transition-colors"
                      data-testid={`points-activity-${i}`}
                    >
                      <div className="w-5 h-5 rounded-md bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                        <Zap className="w-2.5 h-2.5 text-amber-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] text-zinc-300 truncate">{a.description}</p>
                        <p className="text-[9px] text-zinc-600">{new Date(a.created_at).toLocaleDateString()}</p>
                      </div>
                      <span className={`text-[10px] font-mono font-bold ${a.points >= 0 ? 'text-amber-500' : 'text-red-400'}`}>
                        {a.points >= 0 ? '+' : ''}{a.points}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
